// Live page context for the assistant: what the user is looking at, and which rows they have
// selected. Keep it import-free, so `node --test` can run pageContext.test.ts against it directly.
//
// Carries ids and metadata only, never cell values: the agent re-reads every value through its own
// tools with the caller's token, so an answer never depends on what the browser said and a 200-row
// selection cannot eat the token budget. The agent re-applies every cap below, since anyone can
// craft the request body, so these are a courtesy and not a control.

/** Ids sent per turn. Beyond this the selection is reported as a count plus a truncation marker. */
export const MAX_SELECTED_IDS = 25;

/** Longest single id carried. Longer ids are dropped: a truncated id is not a usable key. */
export const MAX_ID_LENGTH = 100;

export const MAX_ROUTE_LENGTH = 200;
export const MAX_ENTITY_TYPE_LENGTH = 60;

/** Filtered columns reported per list, and values per column. A view filtered wider than this is rare. */
export const MAX_FILTER_COLUMNS = 15;
export const MAX_FILTER_VALUES = 10;
export const MAX_FILTER_LENGTH = 100;

/** Largest row count carried. A grid this size does not exist here, so a bigger number is a bug or a forgery. */
export const MAX_ROW_COUNT = 100_000_000;

/**
 * Reads the grid's row counts at the moment it is called. A getter rather than a reported value: the
 * count changes with every filter, page and data load, and the turn needs the one on screen now.
 */
export type RowCountsReader = () => { total: number; afterFilters: number } | undefined;

/** What one list reports about itself. Stored per entity type, stamped with the route it belongs to. */
export interface ListContextEntry {
  route: string;
  selectedIds: string[];
  /** True while the table's global search box holds a term. */
  searchActive: boolean;
  /** Which columns the grid is filtered by, and the values chosen, so the agent can see the same set. */
  activeFilters: Record<string, string[]>;
  /** Monotonic write order, used to pick the most recently touched list on a page with two of them. */
  sequence: number;
  /** Present once the list has reported one; called only for the list that wins the page. */
  readRowCounts?: RowCountsReader;
}

export type ListContextEntries = Record<string, ListContextEntry>;

/** What a list reports on one interaction. Omitted fields keep their previous value. */
export interface ListContextReport {
  route: string;
  selectedIds?: readonly unknown[];
  searchActive?: boolean;
  activeFilters?: Readonly<Record<string, unknown>>;
  readRowCounts?: RowCountsReader;
}

/** The `data` payload handed to the agent. Ids and metadata only, never cell values. */
export interface PageContextData extends Record<string, unknown> {
  entityType: string;
  selectedIds: string[];
  /** How many rows are actually selected, which is more than `selectedIds.length` when capped. */
  selectedCount: number;
  /** Present only when the id list was capped, so the agent can say the answer is partial. */
  selectedIdsTruncated?: true;
  searchActive: boolean;
  /** Omitted when nothing is filtered, so an unfiltered list says nothing rather than saying "none". */
  activeFilters?: Record<string, string[]>;
  /** How many rows the list holds in total, as the grid counts them. */
  rowsTotal?: number;
  /** How many are left after the user's filters. Omitted when nothing narrows the list. */
  rowsAfterFilters?: number;
}

/** Shape of enso's `AssistantContext`, restated structurally so this module imports nothing. */
export interface AssistantContextShape {
  route?: string;
  data?: Record<string, unknown>;
}

/**
 * The wire shape the agent accepts, mapped explicitly from enso's nested `AssistantContext` rather
 * than forwarded wholesale: the agent's contract is this handful of fields, and a key added to `data`
 * upstream must not silently reach it.
 */
export interface AgentPageContext {
  route?: string;
  entityType?: string;
  selectedIds?: string[];
  selectedCount?: number;
  selectedIdsTruncated?: true;
  searchActive?: true;
  activeFilters?: Record<string, string[]>;
  rowsTotal?: number;
  rowsAfterFilters?: number;
}

// Control characters and angle brackets are stripped so a crafted value cannot forge a delimiter or a
// line of its own in the block the agent renders. The agent sanitizes again; this keeps the wire clean.
const isUnsafeCharacter = (character: string): boolean => {
  const code = character.charCodeAt(0);
  return code < 0x20 || code === 0x7f || character === '<' || character === '>';
};

const sanitize = (value: string, maxLength: number): string =>
  [...value]
    .map(character => (isUnsafeCharacter(character) ? ' ' : character))
    .join('')
    .trim()
    .slice(0, maxLength);

const sanitizeIds = (ids: readonly unknown[]): string[] => {
  const unique = new Set<string>();
  for (const id of ids) {
    if (typeof id !== 'string' && typeof id !== 'number') continue;
    const clean = sanitize(String(id), MAX_ID_LENGTH + 1);
    // A too-long id is dropped rather than cut: a truncated id is not a key the agent can look up.
    if (clean !== '' && clean.length <= MAX_ID_LENGTH) unique.add(clean);
  }
  return [...unique];
};

// A filter value is a value-list key the user picked, not a cell read off a row, so it travels: the agent
// needs the same values to offer a link to the same set. Column names and values are both sanitized, and
// both are capped, because the whole map is client-supplied.
const sanitizeFilters = (filters: Readonly<Record<string, unknown>>): Record<string, string[]> => {
  const clean: Record<string, string[]> = {};

  for (const [column, value] of Object.entries(filters)) {
    if (Object.keys(clean).length >= MAX_FILTER_COLUMNS) break;

    const key = sanitize(column, MAX_FILTER_LENGTH);
    const values = (Array.isArray(value) ? value : [value])
      .filter(entry => typeof entry === 'string' || typeof entry === 'number')
      .map(entry => sanitize(String(entry), MAX_FILTER_LENGTH))
      .filter(entry => entry !== '')
      .slice(0, MAX_FILTER_VALUES);

    if (key !== '' && values.length > 0) clean[key] = values;
  }

  return clean;
};

const emptyEntry = (route: string): ListContextEntry => ({
  route,
  selectedIds: [],
  searchActive: false,
  activeFilters: {},
  sequence: 0,
});

// A count the grid reports is metadata about the view, not a cell value, so it travels. Anything that is
// not a whole number in range is dropped: a wrong count read as fact is worse than no count at all.
const rowCount = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= MAX_ROW_COUNT ? value : undefined;

const nextSequence = (entries: ListContextEntries): number =>
  Object.values(entries).reduce((highest, entry) => Math.max(highest, entry.sequence), 0) + 1;

/** Folds one list's report into the entries map, stamping it with the next write order. */
export const applyListReport = (entries: ListContextEntries, entityType: string, report: ListContextReport): ListContextEntries => {
  const key = sanitize(entityType, MAX_ENTITY_TYPE_LENGTH);
  if (key === '') return entries;

  const route = sanitize(report.route, MAX_ROUTE_LENGTH);
  // A report for a different route replaces the entry instead of merging into it: what the user had
  // selected or searched for on the page they just left says nothing about the page they are on now.
  const previous = entries[key]?.route === route ? entries[key] : emptyEntry(route);

  return {
    ...entries,
    [key]: {
      route,
      selectedIds: report.selectedIds ? sanitizeIds(report.selectedIds) : previous.selectedIds,
      searchActive: report.searchActive ?? previous.searchActive,
      activeFilters: report.activeFilters ? sanitizeFilters(report.activeFilters) : previous.activeFilters,
      readRowCounts: report.readRowCounts ?? previous.readRowCounts,
      sequence: nextSequence(entries),
    },
  };
};

export const removeList = (entries: ListContextEntries, entityType: string): ListContextEntries => {
  const key = sanitize(entityType, MAX_ENTITY_TYPE_LENGTH);
  if (!(key in entries)) return entries;

  const rest = { ...entries };
  delete rest[key];
  return rest;
};

/** Drops every list that belongs to a route the user has navigated away from. */
export const dropOtherRoutes = (entries: ListContextEntries, pathname: string): ListContextEntries => {
  const route = sanitize(pathname, MAX_ROUTE_LENGTH);
  const kept = Object.entries(entries).filter(([, entry]) => entry.route === route);
  return kept.length === Object.keys(entries).length ? entries : Object.fromEntries(kept);
};

// A page can hold two lists (Fees shows fee definitions and fee profiles), so one has to win. A list
// with a selection always beats one without, because a selection is what the user is pointing at;
// otherwise the most recently touched list wins.
const beats = (candidate: ListContextEntry, current: ListContextEntry): boolean => {
  const candidateHasSelection = candidate.selectedIds.length > 0;
  const currentHasSelection = current.selectedIds.length > 0;
  if (candidateHasSelection !== currentHasSelection) return candidateHasSelection;
  return candidate.sequence > current.sequence;
};

const pickList = (entries: ListContextEntries, route: string): [string, ListContextEntry] | null => {
  let best: [string, ListContextEntry] | null = null;
  for (const [entityType, entry] of Object.entries(entries)) {
    if (entry.route !== route) continue;
    if (best === null || beats(entry, best[1])) best = [entityType, entry];
  }
  return best;
};

/**
 * Builds the per-turn context: always the route, plus the winning list on that route when there is one.
 * A route with no list still reports the route, so the agent knows which page the user is on.
 */
export const buildAssistantContext = (entries: ListContextEntries, pathname: string): AssistantContextShape => {
  const route = sanitize(pathname, MAX_ROUTE_LENGTH);
  const picked = pickList(entries, route);
  if (picked === null) return { route };

  const [entityType, entry] = picked;
  const selectedCount = entry.selectedIds.length;
  const selectedIds = entry.selectedIds.slice(0, MAX_SELECTED_IDS);
  const data: PageContextData = {
    entityType,
    selectedIds,
    selectedCount,
    searchActive: entry.searchActive,
  };
  if (selectedCount > selectedIds.length) data.selectedIdsTruncated = true;
  if (Object.keys(entry.activeFilters).length > 0) data.activeFilters = entry.activeFilters;

  const counts = readRowCounts(entry);
  if (counts?.total !== undefined) data.rowsTotal = counts.total;
  // Only when the filters actually narrow the list: "744 of 744" reads as a filtered view when it is not one.
  if (counts?.afterFilters !== undefined && counts.total !== undefined && counts.afterFilters < counts.total)
    data.rowsAfterFilters = counts.afterFilters;

  return { route, data };
};

// The reader comes from the grid, so a throw here is the browser's problem and must not cost the turn
// its whole context.
const readRowCounts = (entry: ListContextEntry): { total?: number; afterFilters?: number } | undefined => {
  if (entry.readRowCounts === undefined) return undefined;

  try {
    const counts = entry.readRowCounts();
    if (counts === undefined) return undefined;
    return { total: rowCount(counts.total), afterFilters: rowCount(counts.afterFilters) };
  } catch {
    return undefined;
  }
};

/** Flattens the context onto the agent's wire shape, keeping only the fields it declares. */
export const buildAgentContext = (context: AssistantContextShape | undefined): AgentPageContext | undefined => {
  const route = typeof context?.route === 'string' && context.route !== '' ? context.route : undefined;
  const data = context?.data as Partial<PageContextData> | undefined;
  if (route === undefined && data === undefined) return undefined;
  if (typeof data?.entityType !== 'string') return { route };

  const selectedIds = (Array.isArray(data.selectedIds) ? data.selectedIds : []).filter(id => typeof id === 'string').slice(0, MAX_SELECTED_IDS);
  return {
    route,
    entityType: data.entityType,
    selectedIds,
    selectedCount: typeof data.selectedCount === 'number' ? data.selectedCount : selectedIds.length,
    selectedIdsTruncated: data.selectedIdsTruncated === true ? true : undefined,
    searchActive: data.searchActive === true ? true : undefined,
    // Re-sanitized rather than forwarded: `data` crosses enso's config, so what reaches the wire is
    // whatever this module can vouch for, and an empty map is left out entirely.
    activeFilters: activeFilters(data.activeFilters),
    rowsTotal: rowCount(data.rowsTotal),
    rowsAfterFilters: rowCount(data.rowsAfterFilters),
  };
};

const activeFilters = (value: unknown): Record<string, string[]> | undefined => {
  if (value === null || typeof value !== 'object') return undefined;

  const clean = sanitizeFilters(value as Record<string, unknown>);
  return Object.keys(clean).length > 0 ? clean : undefined;
};

/** The part of an enso `AssistantMessage` the replay needs. Restated structurally so this module imports nothing. */
export interface ReplayedMessage {
  role: string;
  content?: string;
  toolCalls?: { status?: string; result?: string }[];
}

// What the browser made of the instructions in a turn, in the words the panel showed. The agent's own tool
// result says what it proposed; only these say whether the page carried it out, so a proposal the grid
// refused reads back to the model as refused instead of as one that opened.
const browserOutcomes = (entry: ReplayedMessage): string[] =>
  (entry.toolCalls ?? [])
    .filter(call => call.status === 'success' || call.status === 'error')
    .map(call => (typeof call.result === 'string' ? call.result.trim() : ''))
    .filter(result => result !== '');

/** One turn as the agent reads it back: what the assistant said, and what the page did about it. */
export const replayedContent = (entry: ReplayedMessage): string => {
  const said = entry.content ?? '';
  if (entry.role !== 'assistant') return said;

  const outcomes = browserOutcomes(entry);
  return outcomes.length === 0 ? said : `${said}\n\n[the portal: ${outcomes.join('; ')}]`;
};
